// @ts-nocheck
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    isRequired = "isRequired",
    fieldLabel = "fieldLabel",
    jobTitleOptions = "jobTitleOptions",
    jobTitle = "jobTitle",
}

export interface JobTitleProps extends WidgetProps {
    fields: {
        [fields.isRequired]: boolean;
        [fields.fieldLabel]: string;
        [fields.jobTitleOptions]: {
            fields: {
                [fields.jobTitle]: string;
            };
        }[];
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    jobTitle: state.components.contactUsForm.fieldValues["jobTitle"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = JobTitleProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface JobTitleStyles {
    jobTitleSelect?: SelectPresentationProps;
}

export const jobTitleStyles: JobTitleStyles = {};

const styles = jobTitleStyles;

const JobTitle: FC<Props> = ({ fields, jobTitle, setFieldValue }) => {
    const { isRequired, fieldLabel, jobTitleOptions } = fields;
    const contactFormContext = useContext(ContactFormContext);
    const [jobTitleErrorMessage, setJobTitleErrorMessage] = useState("");
    const [hasValidated, setHasValidated] = useState(false);
    const filteredJobTitleOptions = jobTitleOptions.filter(x => !!x.fields.jobTitle);

    useEffect(() => {
        contactFormContext.validators.jobTitle = validateJobTitle;
    }, [jobTitle]);

    useEffect(() => {
        if (hasValidated) {
            validateJobTitle();
        }
    }, [jobTitle, hasValidated]);

    useEffect(() => {
        contactFormContext.validators.jobTitle = validateJobTitle;
    }, [isRequired]);

    const validateJobTitle = (value: string | undefined = jobTitle) => {
        setHasValidated(true);
        const hasValidSelection = Boolean(value);
        const errorMessage =
            isRequired && !hasValidSelection ? siteMessage("Field_Required", fieldLabel).toString() : "";
        setJobTitleErrorMessage(errorMessage);
        return !errorMessage;
    };

    const jobTitleChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({ key: "jobTitle", value: event.target.value });
        if (hasValidated) {
            validateJobTitle(event.target.value);
        }
    };

    return (
        <Select
            {...styles.jobTitleSelect}
            label={fieldLabel}
            required={isRequired}
            value={jobTitle ?? ""}
            onChange={jobTitleChangeHandler}
            error={jobTitleErrorMessage}
            data-test-selector="contact-us-job-title-select"
        >
            <option value="">{translate("Select a job title")}</option>
            {filteredJobTitleOptions.map(x => (
                <option value={x.fields.jobTitle} key={x.fields.jobTitle}>
                    {x.fields.jobTitle}
                </option>
            ))}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(JobTitle),
    definition: {
        displayName: "Job Title",
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.fieldLabel,
                displayName: "Field Label",
                editorTemplate: "TextField",
                defaultValue: "Job Title",
                fieldType: "Translatable",
            },
            {
                name: fields.jobTitleOptions,
                displayName: "Job Title Options",
                editorTemplate: "ListField",
                getDisplay: item => {
                    return item.fields.jobTitle;
                },
                defaultValue: [
                    {
                        fields: {
                            jobTitle: "Accounts Payable",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Administrative assistant",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Buyer",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Buyer's assistant",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Quality Control",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Planner",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Plant manager",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Production",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Supply chain",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Warehouse manager",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Sustainability / Recycling",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Office coordinator",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Office manager",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Owner of business",
                        },
                    },
                    {
                        fields: {
                            jobTitle: "Other",
                        },
                    },
                ],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: fields.jobTitle,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
